// API Configuration
export const API_CONFIG = {
  baseURL: "https://staging.api.saathi4u.com",
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
} as const;

export const API_ENDPOINTS = {
  auth: {
    login: '/auth/admin/login',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    forgotPassword: '/auth/admin/forgot-password',
    resetPassword: '/auth/admin/reset-password',
  },
  users: {
    list: '/admin/user/all',
    search: '/admin/user/search',
    get: (id: string) => `/admin/user/${id}`,
    block: (id: string) => `/admin/user/block/${id}`,
    unblock: (id: string) => `/admin/user/unblock/${id}`,
    create: '/admin/user',
    update: (id: string) => `/admin/user/${id}`,
    delete: (id: string) => `/admin/user/${id}`,
    hide: (id: string) => `/admin/user/hide/${id}`,
    unhide: (id: string) => `/admin/user/unhide/${id}`,
    assignPlan: '/admin/user/assign-plan',
    bulkExport: '/admin/user/bulk/export',
    sendInvite: (id: string | number) => `/admin/user/${id}/send-invite`,
    connections: (userId: string, tab: string) =>
      `/admin/user/${userId}/connections/${tab}`,
    internalNotes: (userId: string) => `/admin/user/${userId}/internal-notes`,
    internalNote: (userId: string, noteId: string) =>
      `/admin/user/${userId}/internal-notes/${noteId}`,
    invoices: (userId: string) => `/admin/user/${userId}/invoices`,
    invoiceDownload: (userId: string) => `/admin/user/${userId}/invoices/download`,
    tasks: (userId: string) => `/admin/user/${userId}/tasks`,
  },
  userPlans: {
    list: '/admin/user/all-plans',
  },
  invoices: {
    download: '/admin/invoices/download',
    summary: '/admin/invoices/summary',
    export: '/admin/invoices/export',
    send: '/admin/invoices/send',
  },
  employees: {
    list: '/admin/employee/all',
    search: '/admin/employee/search',
    roles: '/admin/employee/meta/roles',
    get: (id: string | number) => `/admin/employee/${id}`,
    create: '/admin/employee',
    update: (id: string | number) => `/admin/employee/${id}`,
    deactivate: (id: string | number) => `/admin/employee/${id}/deactivate`,
    activate: (id: string | number) => `/admin/employee/${id}/activate`,
    sendInvite: (id: string | number) => `/admin/employee/${id}/send-invite`,
  },
  branches: {
    list: '/admin/branch',
    get: (id: string | number) => `/admin/branch/${id}`,
    create: '/admin/branch',
    update: (id: string | number) => `/admin/branch/${id}`,
    delete: (id: string | number) => `/admin/branch/${id}`,
  },
  kanban: {
    board: '/admin/tasks/board',
    create: '/admin/tasks',
    get: (id: string) => `/admin/tasks/${id}`,
    update: (id: string) => `/admin/tasks/${id}`,
    updateStatus: (id: string) => `/admin/tasks/${id}/status`,
    delete: (id: string) => `/admin/tasks/${id}`,
    history: (id: string) => `/admin/tasks/${id}/history`,
    assignees: '/admin/tasks/meta/assignees',
    reporters: '/admin/tasks/meta/reporters',
    comments: (taskId: string) => `/admin/tasks/${taskId}/comments`,
    comment: (taskId: string, commentId: string) =>
      `/admin/tasks/${taskId}/comments/${commentId}`,
    taskTypes: '/admin/task-types',
    labels: '/admin/task-labels',
    labelPalette: '/admin/task-labels/palette',
    label: (id: string) => `/admin/task-labels/${id}`,
  },
  leads: {
    search: '/admin/leads/search',
    get: (id: string) => `/admin/leads/${id}`,
    create: '/admin/leads',
    update: (id: string) => `/admin/leads/${id}`,
    delete: (id: string) => `/admin/leads/${id}`,
    bulkDelete: '/admin/leads/bulk/delete',
    bulkExport: '/admin/leads/bulk/export',
    bulkCreateTask: '/admin/leads/bulk/create-task',
    import: '/admin/leads/import',
    sources: '/admin/lead-sources',
    createSource: '/admin/lead-sources',
  },
  dashboard: {
    stats: '/admin/dashboard/stats',
    upcomingBirthdays: '/admin/dashboard/upcoming-birthdays',
    sendBirthdayEmail: (userId: string | number) =>
      `/admin/dashboard/upcoming-birthdays/${userId}/send-email`,
    tasks: '/admin/dashboard/tasks',
    activityFeed: '/admin/dashboard/activity-feed',
    alerts: '/admin/dashboard/alerts',
  },
  suggestions: {
    pendingSearch: '/admin/suggestions/pending/search',
    clientSearch: (targetUserId: string) =>
      `/admin/suggestions/${targetUserId}/suggested/search`,
    search: (targetUserId: string) => `/admin/suggestions/${targetUserId}/search`,
    send: (targetUserId: string) => `/admin/suggestions/${targetUserId}/send`,
    approve: (targetUserId: string) => `/admin/suggestions/${targetUserId}/approve`,
    reject: (targetUserId: string) => `/admin/suggestions/${targetUserId}/reject`,
  },
  plans: {
    list: '/admin/plan',
    get: (id: string) => `/admin/plan/${id}`,
    create: '/admin/plans',
    update: (id: string) => `/admin/plan/${id}`,
    delete: (id: string) => `/admin/plan/${id}`,
    allDetails: '/admin/plan/all-details',
    detailById: (planDetailId: string) => `/admin/plan/detail/${planDetailId}`,
    variants: {
      list: (planId: string) => `/admin/plan/${planId}/variants`,
      get: (planId: string, variantId: string) => `/admin/plan/${planId}/variants/${variantId}`,
      create: (planId: string) => `/admin/plan/${planId}/variants`,
      update: (planId: string, variantId: string) => `/admin/plan/${planId}/variants/${variantId}`,
      delete: (planId: string, variantId: string) => `/admin/plan/${planId}/variants/${variantId}`,
    },
  },
  auditLogs: {
    create: '/admin/audit-logs',
    list: '/admin/audit-logs',
    get: (id: string) => `/admin/audit-logs/${id}`,
    export: '/admin/audit-logs/export',
  },
  profile: {
    root: '/admin/profile',
    picture: '/admin/profile/picture',
    notifications: '/admin/profile/notifications',
    password: '/admin/profile/password',
  },
  notifications: {
    list: '/admin/notifications',
    unreadCount: '/admin/notifications/unread-count',
    readAll: '/admin/notifications/read-all',
    read: (id: string | number) => `/admin/notifications/${id}/read`,
    delete: (id: string | number) => `/admin/notifications/${id}/delete`,
  },
  shared: {
    // Basic lookups
    heights: '/shared/get-heights',
    motherTongues: '/shared/get-mother-tongues',
    religions: '/shared/get-religions',
    castes: (religionId: string, sectId?: string) => 
      sectId 
        ? `/shared/get-castes/${religionId}?sect_id=${sectId}` 
        : `/shared/get-castes/${religionId}`,
    subCastes: (casteId: string) => `/shared/get-sub-castes/${casteId}`,
    sects: (religionId: string) => `/shared/get-sects/${religionId}`,
    jamats: (sectId: string) => `/shared/get-jamats/${sectId}`,
    gotras: (religionId: string) => `/shared/get-gotras/${religionId}`,
    
    // Location lookups
    countries: '/shared/get-countries',
    states: (countryId: string) => `/shared/get-states/${countryId}`,
    cities: (stateId: string) => `/shared/get-cities/${stateId}`,
    
    // Personal details lookups
    maritalStatuses: '/shared/get-marital-statuses',
      complexions: '/shared/get-complexions',
      bodyTypes: '/shared/get-body-types',
      disabilities: '/shared/get-disabilities',
      bloodGroups: '/shared/get-blood-groups',
    
    // Income & Education lookups
    degrees: '/shared/get-degrees',
    employedIn: '/shared/get-employed-in',
    occupations: (employedInId: string) => `/shared/get-occupations/${employedInId}`,
    
    // Family lookups
    familyTypes: '/shared/get-family-types',
    familyValues: '/shared/get-family-values',
    familyStatuses: '/shared/get-family-statuses',
    fatherOccupations: '/shared/get-father-occupations',
    motherOccupations: '/shared/get-mother-occupations',
    
    // Lifestyle lookups
    diets: '/shared/get-diets',
    drinkingHabits: '/shared/get-drinking-habits',
    smokingHabits: '/shared/get-smoking-habits',
    
    // Horoscope lookups
    stars: '/shared/get-stars',
    raasis: '/shared/get-raasis',
    doshas: '/shared/get-doshas',
    manglikStatuses: '/shared/get-manglik-statuses',
    
    // Profile lookups
    profileCreatedFor: '/shared/get-profile-created-for',
    hobbies: '/shared/get-hobbies',
    interests: '/shared/get-interests',
    
    // File upload
    uploadFiles: '/shared/upload-files',
    uploadUserImage: '/user/upload-user-image',
    uploadUserOtherImages: '/user/upload-user-other-images',
    
    // Currency lookups
    incomeCurrencies: '/shared/get-income-currencies',
    userLocation: '/shared/get-location',
  },
} as const;
